import {
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { sortObjectKeys } from '@vindr/utils/helpers/common.helper';
import useQueryWithCache from '@vindr/utils/hooks/useQueryWithCache';
import {
  ResponseGetStructureTemplate,
  createStructureReport,
  deleteStructureReport,
  getStructureTemplates,
  getStructureTemplate,
  updateStructureReport,
} from '../apis/templateStructuredReport';
import { queryKey } from '../constants/queryClientKey';

export function useGetStructureTemplates(
  param: Parameters<typeof getStructureTemplates>[0],
  options?: UseQueryOptions
) {
  return useQuery(
    [queryKey.structure_report, sortObjectKeys(param)],
    () => getStructureTemplates(param),
    {
      keepPreviousData: true,
      ...options,
      onError: (err) => {
        console.error('useGetStructureTemplates', err);
      },
    }
  ) as UseQueryResult<Awaited<ReturnType<typeof getStructureTemplates>>>;
}

export function useGetDetailStructureTemplate(
  id: Parameters<typeof getStructureTemplate>[0],
  options?: Parameters<typeof useQueryWithCache>[0]['options']
) {
  return useQuery(
    [queryKey.template_detail, id],
    () => getStructureTemplate(id),
    {
      keepPreviousData: true,
      ...options,
    }
  ) as UseQueryResult<ResponseGetStructureTemplate['data']>;
}
/*
export const useGetElementInfinity = (
  params: Parameters<typeof getElement>[0],
  options?: UseInfiniteQueryOptions
) => {
  const queryInfo = useInfiniteQuery(
    [queryKey.share_infinite, sortObjectKeys(params)],
    (e) =>
      getElement({
        ...params,
        offset: (e.pageParam || 0) + (params?.offset || 0),
      }),
    {
      getNextPageParam: (lastPage: any, allPages) => {
        if (lastPage.data.data.length < params.limit) return undefined;
        return allPages.reduce((acc, page) => acc + page.data?.data.length, 0);
      },
      keepPreviousData: true,
      ...options,
    }
  ) as UseInfiniteQueryResult<Awaited<ReturnType<typeof getElement>>>;

  const data = queryInfo.data?.pages.map((page) => page.data.data || []).flat();

  return {
    ...queryInfo,
    data,
    count: queryInfo.data?.pages[0].count,
  };
}; */

export function useCreateStructureReport() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: Parameters<typeof createStructureReport>[0]) =>
      createStructureReport(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.structure_report]);
      },
      onError: (err) => {
        console.error('useCreateStructureReport', err);
      },
    }
  );
}

export function useUpdateStructureReport() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      id,
      data,
    }: {
      id: Parameters<typeof updateStructureReport>[0];
      data: Parameters<typeof updateStructureReport>[1];
    }) => updateStructureReport(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.structure_report]);
        queryClient.invalidateQueries([queryKey.template_detail]);
      },

      onError: (err) => {
        console.error('useUpdateStructureReport', err);
      },
    }
  );
}

export function useDeleteStructureReport() {
  const queryClient = useQueryClient();

  return useMutation(async (id: string) => deleteStructureReport(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.structure_report]);
    },

    onError: (err) => {
      console.error('deleteStructureReport', err);
    },
  });
}
